import { ArrowRight } from "lucide-react"
import { useEffect } from "react"
import { Link, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { consultationDateLabel } from "@/lib/consultation-schedule"
import { captureAnalyticsEvent, getAnalyticsIdentity } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"
import type { MarketingPageProps } from "@/types/marketing"

type ConsultationData = {
  company_name: string
  contact_name: string
  email: string
  fax_number: string
  message: string
  posthog_distinct_id: string
  privacy_acknowledged: boolean
  requested_date: string
  requested_start_at: string
  requested_time: string
  submission: string
  website_url: string
}

type Props = PageProps<MarketingPageProps>

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null

  return <p className="website-review-error type-marketing-caption" id={id} role="alert">{message}</p>
}

export default function MarketingContact(props: Props) {
  const config = props.consultation_form!
  const form = useForm<ConsultationData>({
    company_name: "",
    contact_name: "",
    email: "",
    fax_number: "",
    message: "",
    posthog_distinct_id: "",
    privacy_acknowledged: false,
    requested_date: "",
    requested_start_at: "",
    requested_time: "",
    submission: "",
    website_url: "",
  })
  const errors = { ...props.errors, ...form.errors }
  const copy = props.locale === "hr" ? {
    contactName: "Ime i prezime",
    email: "Poslovna e-adresa",
    company: "Naziv tvrtke",
    website: "Adresa web-stranice",
    optional: "Opcionalno",
    date: "Željeni datum",
    time: "Željeno vrijeme",
    chooseDate: "Odaberite radni dan",
    chooseTime: "Odaberite vrijeme",
    context: "Što želite poboljšati ili automatizirati?",
    consent: "Pročitao/la sam Obavijest o privatnosti i želim da Zmile obradi ove podatke kako bi odgovorio na moj zahtjev.",
    privacy: "Obavijest o privatnosti",
    submit: "Pošaljite zahtjev za termin",
    submitting: "Slanje…",
    holidays: "Ponuđeni su radni dani od 09:00 do 15:30 po vremenu Europe/Zagreb. Hrvatski državni praznici nisu automatski isključeni u beta izdanju.",
  } : {
    contactName: "Contact name",
    email: "Work email",
    company: "Company name",
    website: "Website URL",
    optional: "Optional",
    date: "Preferred date",
    time: "Preferred time",
    chooseDate: "Choose a weekday",
    chooseTime: "Choose a time",
    context: "What would you like to improve or automate?",
    consent: "I have read the Privacy notice and want Zmile to process this information to respond to my request.",
    privacy: "Privacy notice",
    submit: "Submit time request",
    submitting: "Submitting…",
    holidays: "Weekday times run from 09:00 to 15:30 in Europe/Zagreb. Croatian public holidays are not automatically excluded during beta.",
  }
  const expectations = props.locale === "hr"
    ? ["Besplatan razgovor od 30 minuta", "Izravno s Antonijem", "Termin potvrđujemo zasebnom e-poštom", "Bez obveze", "Cilj je procijeniti postoji li koristan sljedeći korak"]
    : ["A free 30-minute conversation", "Directly with Antonio", "The time is confirmed in a separate email", "No commitment", "The goal is to decide whether there is a useful next step"]

  useEffect(() => {
    captureAnalyticsEvent("consultation_form_viewed", {
      locale: props.locale,
      page_id: props.page_key,
      lead_kind: "consultation",
    })
    const distinctId = getAnalyticsIdentity()
    if (distinctId) form.setData("posthog_distinct_id", distinctId)
  }, [])

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const distinctId = getAnalyticsIdentity() || form.data.posthog_distinct_id
    form.transform((data) => ({
      authenticity_token: config.authenticity_token,
      form_started_at: config.started_at,
      fax_number: data.fax_number,
      lead: {
        company_name: data.company_name,
        contact_name: data.contact_name,
        email: data.email,
        message: data.message,
        posthog_distinct_id: distinctId,
        privacy_acknowledged: data.privacy_acknowledged,
        requested_date: data.requested_date,
        requested_time: data.requested_time,
        website_url: data.website_url,
      },
    }))
    form.post(config.action, {
      preserveScroll: true,
      onSuccess: () => captureAnalyticsEvent("consultation_requested", {
        locale: props.locale,
        page_id: props.page_key,
        lead_kind: "consultation",
      }),
    })
  }

  return (
    <>
      <MarketingMetadata alternateUrls={props.alternate_urls} canonicalUrl={props.canonical_url} description={props.description} locale={props.locale} noindex={props.noindex} socialImageUrl={props.social_image_url} structuredData={{ "@context": "https://schema.org", "@type": "ContactPage", name: props.title, description: props.description, url: props.canonical_url }} title={props.title} />
      <MarketingLayout consultationHref={props.consultation_href} currentPageKey={props.page_key} footer={props.footer} homeHref={props.home_href} languageSwitch={props.language_switch} locale={props.locale} navigation={props.navigation}>
        <section className="contact-booking-hero">
          <div>
            <h1 className="type-marketing-hero-display">{props.heading}</h1>
            <p className="type-marketing-lead">{props.summary}</p>
            <ol>{expectations.map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>)}</ol>
          </div>
          <div className="contact-booking-calendar">
            <form action={config.action} className="consultation-request-form website-review-form" method="post" noValidate onSubmit={submit}>
              <input name="authenticity_token" type="hidden" value={config.authenticity_token} />
              <input name="form_started_at" type="hidden" value={config.started_at} />
              <input name="lead[posthog_distinct_id]" type="hidden" value={form.data.posthog_distinct_id} />
              <div aria-hidden="true" className="website-review-honeypot">
                <label htmlFor="fax_number">Fax</label>
                <Input autoComplete="off" id="fax_number" name="fax_number" onChange={(event) => form.setData("fax_number", event.target.value)} tabIndex={-1} value={form.data.fax_number} />
              </div>

              {errors.submission && <p className="website-review-submission-error type-marketing-body-small" role="alert">{errors.submission}</p>}

              <div className="website-review-fields-two">
                <div className="website-review-field">
                  <label htmlFor="contact_name">{copy.contactName}</label>
                  <Input aria-describedby={errors.contact_name ? "contact_name_error" : undefined} aria-invalid={Boolean(errors.contact_name)} autoComplete="name" id="contact_name" name="lead[contact_name]" onChange={(event) => form.setData("contact_name", event.target.value)} required value={form.data.contact_name} />
                  <FieldError id="contact_name_error" message={errors.contact_name} />
                </div>
                <div className="website-review-field">
                  <label htmlFor="email">{copy.email}</label>
                  <Input aria-describedby={errors.email ? "email_error" : undefined} aria-invalid={Boolean(errors.email)} autoComplete="email" id="email" name="lead[email]" onChange={(event) => form.setData("email", event.target.value)} required type="email" value={form.data.email} />
                  <FieldError id="email_error" message={errors.email} />
                </div>
              </div>

              <div className="website-review-fields-two">
                <div className="website-review-field">
                  <label htmlFor="company_name">{copy.company}</label>
                  <Input aria-describedby={errors.company_name ? "company_name_error" : undefined} aria-invalid={Boolean(errors.company_name)} autoComplete="organization" id="company_name" name="lead[company_name]" onChange={(event) => form.setData("company_name", event.target.value)} required value={form.data.company_name} />
                  <FieldError id="company_name_error" message={errors.company_name} />
                </div>
                <div className="website-review-field">
                  <label htmlFor="website_url">{copy.website} <span className="type-marketing-caption">({copy.optional})</span></label>
                  <Input aria-describedby={errors.website_url ? "website_url_error" : undefined} aria-invalid={Boolean(errors.website_url)} autoComplete="url" id="website_url" inputMode="url" name="lead[website_url]" onChange={(event) => form.setData("website_url", event.target.value)} placeholder="https://example.com" value={form.data.website_url} />
                  <FieldError id="website_url_error" message={errors.website_url} />
                </div>
              </div>

              <fieldset className="consultation-slot-fields">
                <legend>{props.locale === "hr" ? "Zatraženi termin (Europe/Zagreb)" : "Requested time (Europe/Zagreb)"}</legend>
                <div className="website-review-fields-two">
                  <div className="website-review-field">
                    <label htmlFor="requested_date">{copy.date}</label>
                    <Select aria-describedby={errors.requested_start_at ? "requested_start_at_error" : undefined} aria-invalid={Boolean(errors.requested_start_at)} id="requested_date" name="lead[requested_date]" onChange={(event) => form.setData("requested_date", event.target.value)} required value={form.data.requested_date}>
                      <option disabled value="">{copy.chooseDate}</option>
                      {config.dates.map((date) => <option key={date} value={date}>{consultationDateLabel(date, props.locale)}</option>)}
                    </Select>
                  </div>
                  <div className="website-review-field">
                    <label htmlFor="requested_time">{copy.time}</label>
                    <Select aria-describedby={errors.requested_start_at ? "requested_start_at_error" : undefined} aria-invalid={Boolean(errors.requested_start_at)} id="requested_time" name="lead[requested_time]" onChange={(event) => form.setData("requested_time", event.target.value)} required value={form.data.requested_time}>
                      <option disabled value="">{copy.chooseTime}</option>
                      {config.times.map((time) => <option key={time} value={time}>{time} · 30 min</option>)}
                    </Select>
                  </div>
                </div>
                <FieldError id="requested_start_at_error" message={errors.requested_start_at} />
                <p className="type-marketing-caption type-marketing-muted">{copy.holidays}</p>
              </fieldset>

              <div className="website-review-field">
                <label htmlFor="message">{copy.context}</label>
                <textarea aria-describedby={errors.message ? "message_error" : undefined} aria-invalid={Boolean(errors.message)} className="form-control form-control-textarea" id="message" maxLength={5000} name="lead[message]" onChange={(event) => form.setData("message", event.target.value)} required value={form.data.message} />
                <FieldError id="message_error" message={errors.message} />
              </div>

              <div className="website-review-consent">
                <Checkbox aria-describedby={errors.privacy_acknowledged ? "privacy_acknowledged_error" : undefined} aria-invalid={Boolean(errors.privacy_acknowledged)} checked={form.data.privacy_acknowledged} className="website-review-consent-control" id="privacy_acknowledged" name="lead[privacy_acknowledged]" onChange={(event) => form.setData("privacy_acknowledged", event.target.checked)} required value="1" />
                <div>
                  <label className="font-normal text-ink-body" htmlFor="privacy_acknowledged">{copy.consent} <Link className="marketing-consent-link" href={props.footer.privacy_href}>{copy.privacy}</Link>.</label>
                  <FieldError id="privacy_acknowledged_error" message={errors.privacy_acknowledged} />
                </div>
              </div>

              <Button data-analytics-id="consultation-request-submit" disabled={form.processing} size="marketingLg" type="submit" variant="marketing">
                {form.processing ? copy.submitting : copy.submit}<ArrowRight className="marketing-inline-icon" aria-hidden="true" />
              </Button>
            </form>
          </div>
        </section>
      </MarketingLayout>
    </>
  )
}
